import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/capacitor';
import { get } from 'lodash';
import {
  catchError,
  map,
  mergeMap,
  of,
  retry,
  startWith,
  switchMap,
  throwError,
  timer
} from 'rxjs';
import { Logger } from '@app/utils/logger';
import { AuthActions } from '@components/auth/auth.actions';
import { ReportType } from '@interfaces/schema';
import { QuestionnaireNourishActions } from './nourish.actions';
import { selectQuestionnaireNourishFormForSubmit } from './nourish.selectors';
import { QuestionnaireNourishService } from './nourish.service';

/**
 * @description Questionnaire Nourish Effects.
 * @exports
 * @class QuestionnaireNourishEffects
 */
@Injectable()
export class QuestionnaireNourishEffects extends QuestionnaireNourishService {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);
  private readonly logger = Logger.getInstance(
    QuestionnaireNourishEffects.name
  );

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => QuestionnaireNourishActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onFetchUserProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireNourishActions.fetchUserProfile),
        switchMap(() => {
          // Check network status
          if (!navigator.onLine) {
            return of(
              QuestionnaireNourishActions.setLoading({
                key: 'fetchUserProfile',
                value: false
              })
            );
          }

          return this.fetchUserProfile().pipe(
            retry({
              count: 2,
              /**
               * @description Retry logic for network errors.
               * @param {unknown} error Error object.
               * @param {number} retryCount Current retry attempt count.
               * @returns {Observable<number>} Observable that emits a delay before retrying.
               */
              delay: (error, retryCount) => {
                if (this.isNetworkError(error) && retryCount < 2) {
                  this.logger.info(
                    `[QuestionnaireNourishEffects] Retrying onFetchUserProfile... Attempt ${retryCount + 1}`
                  );
                  return timer(1000 * (retryCount + 1));
                }
                return throwError(() => error as Error);
              }
            }),
            mergeMap((response) => {
              return [
                QuestionnaireNourishActions.setLoading({
                  key: 'fetchUserProfile',
                  value: false
                }),
                QuestionnaireNourishActions.setUserProfile({
                  value: get(response, 'data.results[0]', null)
                })
              ];
            }),
            catchError((error) => {
              this.logger.error(
                '[QuestionnaireNourishEffects] onFetchUserProfile Error:',
                error
              );
              Sentry.withScope((scope) => {
                scope.setTransactionName(
                  '[QuestionnaireNourishEffects] onFetchUserProfile Error:'
                );
                Sentry.captureException(error, scope);
              });
              return of(
                QuestionnaireNourishActions.setLoading({
                  key: 'fetchUserProfile',
                  value: false
                }),
                QuestionnaireNourishActions.setUserProfile({
                  value: null
                })
              );
            }),
            startWith(
              QuestionnaireNourishActions.setLoading({
                key: 'fetchUserProfile',
                value: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );

  readonly onSubmit = createEffect(
    () => {
      return this.actions.pipe(
        ofType(QuestionnaireNourishActions.submit),
        concatLatestFrom(() => [
          this.store.select(selectQuestionnaireNourishFormForSubmit)
        ]),
        switchMap(([, form]) => {
          // Check network status
          if (!navigator.onLine) {
            return of(
              QuestionnaireNourishActions.setLoading({
                key: 'submit',
                value: false
              }),
              QuestionnaireNourishActions.setSubmissionResponse({
                success: false,
                message:
                  'No internet connection. Please check your network and try again.',
                response: null
              })
            );
          }

          return this.submit({
            data: {
              answerQuestion: form,
              email: this.session.getProfile()?.email ?? '',
              reportType: ReportType.Nutrigenetic
            }
          }).pipe(
            retry({
              count: 2,
              /**
               * @description Retry logic for network errors.
               * @param {unknown} error Error object.
               * @param {number} retryCount Current retry attempt count.
               * @returns {Observable<number>} Observable that emits a delay before retrying.
               */
              delay: (error, retryCount) => {
                if (this.isNetworkError(error) && retryCount < 2) {
                  this.logger.info(
                    `[QuestionnaireNourishEffects] Retrying onSubmit... Attempt ${retryCount + 1}`
                  );
                  return timer(1000 * (retryCount + 1));
                }
                return throwError(() => error as Error);
              }
            }),
            mergeMap((response) => [
              QuestionnaireNourishActions.setLoading({
                key: 'submit',
                value: false
              }),
              QuestionnaireNourishActions.setSubmissionResponse({
                success: true,
                message: 'Submission successful',
                response
              })
            ]),
            catchError((error) => {
              this.logger.error(
                '[QuestionnaireNourishEffects] onSubmit Error:',
                error
              );
              Sentry.withScope((scope) => {
                scope.setTransactionName(
                  '[QuestionnaireNourishEffects] onSubmit Error:'
                );
                Sentry.captureException(error, scope);
              });
              const errorMessage = this.getNetworkErrorMessage(error);
              return of(
                QuestionnaireNourishActions.setLoading({
                  key: 'submit',
                  value: false
                }),
                QuestionnaireNourishActions.setSubmissionResponse({
                  success: false,
                  message:
                    errorMessage ?? 'Submission failed. Please try again.',
                  response: null
                })
              );
            }),
            startWith(
              QuestionnaireNourishActions.setLoading({
                key: 'submit',
                value: true
              })
            )
          );
        })
      );
    },
    { dispatch: true }
  );

  /**
   * @description Get a user-friendly error message based on the error object.
   * @private
   * @param {unknown} error Error object to analyze.
   * @returns {string | null} The user-friendly error message or null.
   * @memberof QuestionnaireNourishEffects
   */
  private getNetworkErrorMessage(error: any): string | null {
    if (!navigator.onLine || this.isNetworkError(error)) {
      return 'No internet connection. Please check your network and try again.';
    }
    if (error.status >= 500) {
      return 'Server error. Please try again later.';
    }
    if (error.status === 408) {
      return 'Request timeout. Please try again.';
    }
    return null;
  }

  /**
   * @description Helper methods for network error handling.
   * @private
   * @param {unknown} error Error object to check.
   * @returns {boolean}
   * @memberof QuestionnaireNourishEffects
   */
  private isNetworkError(error: any): boolean {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return (
      (!navigator.onLine ||
        error.status === 0 ||
        error.status === 503 ||
        error.status === 504 ||
        error.name === 'NetworkError' ||
        error.message?.includes('Network')) ??
      error.message?.includes('fetch') ??
      error.message?.includes('Failed to fetch')
    );
  }
}
